import {
  Button,
  Column,
  Columns,
  FlexBox,
  Icon,
  Modal,
  ModalBody,
  Spinner,
} from '@dixa/lib-shared-habla';
import { ampli } from 'lib-shared-tracking';
import { useCallback, useEffect, useRef } from 'react';

import { appActions } from 'app/app.actions';
import { CommandLauncherScope, useCommandLauncher } from 'utils/commandLauncher';
import { createAppModal } from 'utils/modal';
import { useAppDispatch } from 'utils/redux/useAppDispatch';

import styles from './commandLauncher.module.scss';
import { ConversationScopeList } from './components/conversationScopeList/conversationScopeList';
import { MacroScopeList } from './components/macroScopeList/macroScopeList';
import { MainSelectionList } from './components/mainScopeList/mainScopeList';
import { UserScopeList } from './components/userScopeList/userScopeList';
import { useRegisterDefaultActions } from './hooks/useRegisterDefaultActions';

/** Converts CommandLauncherScope to a string for tracking. */
const getScopeName = (scope: CommandLauncherScope): string => {
  switch (scope) {
    case CommandLauncherScope.User:
      return 'user';
    case CommandLauncherScope.Conversation:
      return 'conversation';
    case CommandLauncherScope.Macro:
      return 'macro';
    default:
      return 'main';
  }
};

/**
 * Represents the component for displaying a command launcher that can be
 * triggered with a hotkey.
 */
export const CommandLauncher = createAppModal(
  { type: 'app:commandLauncher', featureFlag: 'cmd-k' },
  ({ isOpen, hide }): JSX.Element => {
    const dispatch = useAppDispatch();
    const { asyncStatus, query, scope, setIsActive, setQuery } =
      useCommandLauncher();
    const inputRef = useRef<HTMLInputElement>(null);

    // Register default navigation actions
    useRegisterDefaultActions();

    // Handles closing the command launcher (via ESC or click outside)
    const handleClose = useCallback(() => {
      ampli.commandPaletteClosed({
        closedBy: 'escape', // Modal handles both ESC and click outside
        query: query || undefined,
        scope: getScopeName(scope ?? CommandLauncherScope.Main),
      });
      setQuery('');
      hide();
    }, [query, scope, hide, setQuery]);

    // Handles resetting the search when the command launcher closes.
    useEffect(() => {
      if (!isOpen) {
        setQuery('');
      }
    }, [isOpen, setQuery]);

    // Focus input when query is programmatically set (e.g., @, #, or !)
    useEffect(() => {
      if (isOpen && (query === '@' || query === '#' || query === '!')) {
        inputRef.current?.focus();
      }
    }, [isOpen, query]);

    // Opens the feedback modal
    const handleFeedbackClick = useCallback(() => {
      dispatch(
        appActions.showModal({
          modal: {
            type: 'app:feedbackForm',
            arg: {
              title: 'Command Launcher feedback',
              team: 'conversations',
              placeholder:
                'Tell us what you think about the Command Launcher (Cmd+K).',
            },
          },
        }),
      );
      hide();
    }, [dispatch, hide]);

    return (
      <Modal
        isOpen={isOpen}
        onClose={handleClose}
        allowClickOutside
        skipAnimation
        className={styles.modal}
      >
        <ModalBody className={styles.container}>
          <Columns className={styles.panel}>
            <Column>
              <Column.Row
                className={styles.search}
                noHorizontalPadding
                noVerticalPadding
                noMinHeight
                borderBottom
              >
                <FlexBox alignItems="center" gap="200">
                  {asyncStatus === 'pending' ? (
                    <Spinner size={16} />
                  ) : (
                    <Icon name="search" />
                  )}

                  <input
                    ref={inputRef}
                    className={styles.input}
                    autoFocus
                    placeholder="Search commands, @ contacts, # conversations, ! macros..."
                    value={query}
                    onFocus={() => setIsActive(true)}
                    onBlur={() => setIsActive(false)}
                    onChange={(event) => setQuery(event.target.value)}
                  />
                </FlexBox>
              </Column.Row>

              <Column.Row
                scrollable
                size={1}
                noHorizontalPadding
                noVerticalPadding
                noMinHeight
                className={styles.results}
              >
                {scope === CommandLauncherScope.Main && <MainSelectionList />}

                {scope === CommandLauncherScope.Conversation && (
                  <ConversationScopeList />
                )}

                {scope === CommandLauncherScope.User && <UserScopeList />}

                {scope === CommandLauncherScope.Macro && <MacroScopeList />}
              </Column.Row>

              <Column.Row
                noHorizontalPadding
                noVerticalPadding
                noMinHeight
                borderTop
                className={styles.hints}
              >
                <FlexBox alignItems="center" justifyContent="space-between">
                  <FlexBox alignItems="center" gap="400">
                    <span>
                      <kbd>↑↓</kbd> Navigate
                    </span>
                    <span>
                      <kbd>↵</kbd> Select
                    </span>
                    <span>
                      <kbd>esc</kbd> Close
                    </span>
                  </FlexBox>
                  <Button variant="tertiary" onClick={handleFeedbackClick}>
                    Give feedback
                  </Button>
                </FlexBox>
              </Column.Row>
            </Column>
          </Columns>
        </ModalBody>
      </Modal>
    );
  },
);
